import { AppDispatch } from '../../../../app/store';
import { TpDataContext, TpDataTable } from '../../../../data/interfaces';
import { FORM_DATATYPES, TpAutoFormDef, promptAutoForm } from '../../../../components/auto-form-modal/AutoForm';
import { showErrorMessage } from '../../../../components/message-boxes/ErrorMessage';
import { TpChartDataBinding, getDataTable, getDataTableByCanonicalType } from '../../charts/chartDataBindings';
import { chartFactory } from '../../charts/factory';
import { chartTypeListForEditCreation } from '../../charts/factory';
import { addNewChart } from '../../data/dashboardsSlice';
import { get_guid } from '../../helpers/misc';
import { isCompatibleDataType } from '../../widget/WidgetMenu';
import { TpDashboardContext } from '../interfaces';

function promptForDataMatrixColumnsBinding(
  dataContext: TpDataContext,
  dashboardContext: TpDashboardContext,
  dataTable: TpDataTable,
) {
  const canonicalTypes: { id: string; content: string }[] = [];
  const compatibleCanonicalTypeIdsMap: { [id: string]: boolean } = {};
  for (const column of dataTable.columns) {
    if (column.columnCanonicalTypeId && !compatibleCanonicalTypeIdsMap[column.columnCanonicalTypeId]) {
      compatibleCanonicalTypeIdsMap[column.columnCanonicalTypeId] = true;
      canonicalTypes.push({
        id: column.columnCanonicalTypeId,
        content: dataContext.canonicalTypes[column.columnCanonicalTypeId].name,
      });
    }
  }
  if (canonicalTypes.length == 0) {
    showErrorMessage('Invalid data table: no matrix associated');
    return;
  }

  const form: TpAutoFormDef = {
    title: 'Data matrix binding',
    fields: [
      {
        id: 'columnsCanonicalTypeId',
        name: 'Columns canonical type',
        dataType: FORM_DATATYPES.MULTIPLECHOICE,
        startContent: '',
        choices: canonicalTypes,
      },
      {
        id: 'columnsGroupId',
        name: 'Columns group',
        dataType: FORM_DATATYPES.MULTIPLECHOICE,
        startContent: '',
        choices: dataTable.columnGroups.map((group) => ({ id: group.id, content: group.name })),
      },
    ],
  };

  return promptAutoForm(form);
}

export function promptAddChart(
  dataContext: TpDataContext,
  dashboardContext: TpDashboardContext,
  parentId: string,
  dispatch: AppDispatch,
) {
  const form: TpAutoFormDef = {
    title: 'Create new chart',
    fields: [
      {
        id: 'dataTable',
        name: 'Data table',
        dataType: FORM_DATATYPES.MULTIPLECHOICE,
        startContent: '',
        choices: dataContext.dataTables.map((dataTable) => ({ id: dataTable.id, content: dataTable.name })),
      },
      {
        id: 'chartType',
        name: 'Chart type',
        dataType: FORM_DATATYPES.MULTIPLECHOICE,
        startContent: '',
        choices: chartTypeListForEditCreation.map((chartType) => ({
          id: chartType,
          content: chartFactory(chartType).name,
        })),
      },
    ],
  };

  promptAutoForm(form).then(
    (formData: any) => {
      const dataTable = getDataTable(dataContext, formData.dataTable);
      const chartTypeManifest = chartFactory(formData.chartType);

      const aspectBindings: { [id: string]: any } = {};
      const columnUsed: { [colId: string]: boolean } = {};
      for (const aspect of chartTypeManifest.aspects)
        if (aspect.required && !aspect.onColumnItems) {
          for (const column of dataTable.columns)
            if (isCompatibleDataType(aspect.dataType, column.valueType))
              if (!aspectBindings[aspect.id] || columnUsed[aspectBindings[aspect.id].columnId])
                aspectBindings[aspect.id] = { columnId: column.id };
          if (!aspectBindings[aspect.id]) {
            showErrorMessage('Not all required aspects could be bound to data table');
            return;
          }
          columnUsed[aspectBindings[aspect.id].columnId] = true;
        }

      const dataBinding: TpChartDataBinding = {
        dataTableId: formData.dataTable,
        aspectBindings,
      };

      function doCreate() {
        dispatch(
          addNewChart({
            dashboardDefId: dashboardContext.dashboardDefId,
            parentId,
            id: get_guid(),
            chartType: formData.chartType,
            dataBinding,
          }),
        );
      }

      if (!chartTypeManifest.showDataMatrix) {
        doCreate();
      } else {
        const dataTable = getDataTable(dataContext, formData.dataTable);
        promptForDataMatrixColumnsBinding(dataContext, dashboardContext, dataTable)?.then(
          (formData: any) => {
            dataBinding.columnsCanonicalTypeId = formData.columnsCanonicalTypeId;
            dataBinding.columnsGroupId = formData.columnsGroupId;

            // Bind aspect that need to be bound to column datatable
            const dataTableColumns = getDataTableByCanonicalType(dataContext, dataBinding.columnsCanonicalTypeId!);
            const columnUsed: { [colId: string]: boolean } = {};
            for (const aspect of chartTypeManifest.aspects)
              if (aspect.required && aspect.onColumnItems) {
                for (const column of dataTableColumns.columns)
                  if (isCompatibleDataType(aspect.dataType, column.valueType))
                    if (!aspectBindings[aspect.id] || columnUsed[aspectBindings[aspect.id].columnId])
                      aspectBindings[aspect.id] = { columnId: column.id };
                if (!aspectBindings[aspect.id]) {
                  showErrorMessage('Not all required aspects could be bound to columns data table');
                  return;
                }
                columnUsed[aspectBindings[aspect.id].columnId] = true;
              }

            doCreate();
          },
          () => {},
        );
      }
    },
    () => {},
  );
}
